import { useState } from 'react';
import KitModal from '../../../UI/KitModal';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Info, Plus, Trash } from '@phosphor-icons/react';
import { useAuthStore } from '../../../../store/storeAuth';
import IconButtonTooltip from '../../../UI/IconButtonTooltip/IconButtonTooltip';
import { useObjectStore } from '../../../../store/storeObjects';
import { CompanySpacesService } from '../../../../service/CompanySpacesService';
import { UserService } from '../../../../service/UserService';
import { AuthService } from '../../../../service/AuthService';
import {
  NotificationType,
  useNotificationStore,
} from '../../../../store/storeNotifications';

const ObjectModal = ({
  show,
  onHide,
  flowId = '',
  spaceId = 'user',
  selectObject = () => {},
}: any) => {
  const [isLoading, setIsLoading] = useState(false);

  const { user, setAuthUser } = useAuthStore(state => state);
  const setNotification = useNotificationStore(state => state.setNotification);
  const {
    objectUpdate,
    setObjectUpdate,
    addObject,
    updateObject,
    getObjectsBySpaceId,
  } = useObjectStore(state => state);

  const onObjectChange = (e: {
    target: { name: any; value: any; checked?: any };
  }) => {
    if (e.target.name === 'objectName') {
      setObjectUpdate({ ...objectUpdate, name: e.target.value });
    } else if (e.target.name === 'objectLocal') {
      const isLocal = !objectUpdate.localObject;
      setObjectUpdate({
        ...objectUpdate,
        localObject: isLocal,
        flowId: isLocal ? flowId : '',
      });
    } else {
      const modifiedObjectTexts = objectUpdate.text.map((text: any) => {
        if (e.target.name === text.id) {
          return {
            ...text,
            text: e.target.value,
          };
        }
        return text;
      });
      setObjectUpdate({ ...objectUpdate, text: modifiedObjectTexts });
    }
  };

  const onSubmit = async () => {
    setIsLoading(true);

    if (objectUpdate?.isCreating) {
      addObject(objectUpdate, spaceId);
    } else {
      updateObject(objectUpdate.id, objectUpdate, spaceId);
    }

    const objects = getObjectsBySpaceId(spaceId);

    if (spaceId === 'user') {
      await UserService.updateUser({ objects });

      const userRes = await AuthService.getMe();
      setAuthUser(userRes?.data);
    } else {
      await CompanySpacesService.updateCompanySpace(user?.selectedCompanyId!, {
        spaceId,
        objects,
      });
    }

    objectUpdate.isCreating
      ? setNotification({
          type: NotificationType.Success,
          message: 'Object created successfully',
        })
      : setNotification({
          type: NotificationType.Success,
          message: 'Object updated successfully',
        });

    setIsLoading(false);
    selectObject(objectUpdate.id);
    onHide();
  };

  const addNewElement = () => {
    const newElement = {
      id: Math.random().toString(36).substr(2, 9),
      text: '',
    };

    setObjectUpdate({
      ...objectUpdate,
      text: [...objectUpdate.text, newElement],
    });
  };

  const onRemoveElement = (id: string) => {
    const modifiedObjectTexts = objectUpdate.text.filter(
      (text: any) => text.id !== id,
    );
    setObjectUpdate({ ...objectUpdate, text: modifiedObjectTexts });
  };

  return (
    <KitModal
      show={show}
      onHide={onHide}
      fullWidth
      sx={{
        '& .MuiPaper-root': {
          maxWidth: '650px',
          borderRadius: '16px',
        },
        '& .MuiDialogContent-root': {
          padding: '16px',
        },
        '& .MuiDialogActions-root': {
          padding: '16px',
        },
      }}
      footer={
        <Box>
          <Button onClick={addNewElement} disabled={isLoading}>
            <Plus size={15} style={{ marginRight: '4px' }} /> Add element
          </Button>

          <Button
            disabled={isLoading}
            variant="contained"
            onClick={onSubmit}
            style={{
              marginLeft: '8px',
            }}
          >
            {objectUpdate?.isCreating ? 'Create' : 'Update'}
          </Button>
        </Box>
      }
    >
      <Box>
        <Typography
          variant="h6"
          color={user?.theme === 'dark' ? '#D0D5DD' : 'black'}
        >
          {objectUpdate?.isCreating ? 'Create Object' : 'Update Object'}
        </Typography>
        <Box
          sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
          my={'16px'}
          borderRadius={'4px'}
        >
          <TextField
            name="objectName"
            placeholder="Enter object's name"
            value={objectUpdate.name}
            onChange={onObjectChange}
            fullWidth
            variant="outlined"
            label="Object's Name"
          />
        </Box>

        <Divider sx={{ mb: '20px' }} />
        {objectUpdate?.text?.map((item: any) => {
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                gap: '20px',
                width: '100%',
                alignContent: 'center',
                mb: 2,
              }}
              key={item?.id}
              className="input-group-prepend"
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  gap: '20px',
                }}
              >
                <textarea
                  className="nowheel nodrag"
                  placeholder="Enter element's value"
                  value={item?.text}
                  name={item.id}
                  onChange={onObjectChange}
                  style={{
                    maxWidth: '100%',
                    width: '100%',
                    height: '40px',
                    borderRadius: '8px',
                    border: '1px solid #E0E0E0',
                    padding: '10px',
                    fontSize: '14px',
                    fontWeight: '400',
                    fontFamily: 'inherit',
                    color: '#000000',
                    resize: 'vertical',
                  }}
                />

                <IconButton
                  color={'error'}
                  onClick={() => onRemoveElement(item?.id)}
                >
                  <Trash size={20} />
                </IconButton>
              </Box>
            </Box>
          );
        })}

        {flowId && (
          <Box>
            <Typography
              variant="h6"
              color={user?.theme === 'dark' ? '#D0D5DD' : 'black'}
            >
              Object Type
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <IconButtonTooltip
                title={
                  'Local setting means this object can only be seen, selected, and used within this agent. It will not be visible in any other agent or on the left-navigation space.'
                }
              >
                <Typography color="#98A2B3" fontSize={'14px'}>
                  Local
                </Typography>
                <Info color="#98A2B3" size={12} style={{ marginLeft: '4px' }} />
              </IconButtonTooltip>
              <Switch
                name="objectLocal"
                size="small"
                onChange={(e: any) => onObjectChange(e)}
                checked={!objectUpdate.localObject}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#1976d2',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#1976d2',
                  },
                  '& .MuiSwitch-thumb': {
                    backgroundColor: '#1976d2',
                  },
                  '& .MuiSwitch-track': {
                    backgroundColor: '#1976d2',
                    opacity: 0.5,
                  },
                }}
              />
              <IconButtonTooltip
                title={
                  'Global setting means this object can be used or referenced by any and all agents within this space.'
                }
              >
                <Typography color="#98A2B3" fontSize={'14px'}>
                  Global
                </Typography>
                <Info color="#98A2B3" size={12} style={{ marginLeft: '4px' }} />
              </IconButtonTooltip>
            </Box>
          </Box>
        )}
      </Box>
    </KitModal>
  );
};

export default ObjectModal;
